import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users as UsersIcon } from 'react-feather';

import '../../../ControlPanel/sadmin.css';
import '../../Settings/settings.css';
import '../admin-v3.css';

import ManageUsers from '../Users/ManageUsers';
import ManageProfile from '../UserProfile/ManageProfile';

/**
 * /admin/usermanagement — "Users & Roles" hub.
 * Hosts the user-accounts list and the role/permission profiles as two tabs,
 * each gated by its own permission. Reuses the existing ManageUsers /
 * ManageProfile components in `embedded` mode (no own shell/hero).
 */
const UserManagement = () => {
    const { t } = useTranslation();
    const canUsers = localStorage.getItem('manageUsers') === '1';
    const canRoles = localStorage.getItem('manageUserProfile') === '1';

    const tabs = useMemo(() => [
        ...(canUsers ? [{ id: 'users', label: 'Users' }] : []),
        ...(canRoles ? [{ id: 'roles', label: 'Roles & Permissions' }] : []),
    ], [canUsers, canRoles]);

    const [tab, setTab] = useState(tabs[0]?.id || '');

    useEffect(() => {
        if (!canUsers && !canRoles) {
            window.alert(t('You are not authorized to view this page'));
            window.history.back();
        }
    }, [canUsers, canRoles]);

    if (!canUsers && !canRoles) return null;

    return (
        <div className="v3-sadmin v3-admin-page">
            <div className="v3-sadmin-hero">
                <div className="v3-sadmin-hero-text">
                    <p className="v3-label" style={{ margin: 0 }}>{t('Admin settings')}</p>
                    <h1 className="v3-display v3-sadmin-greeting">
                        <span className="v3-sadmin-name">{t('Users & Roles')}</span>
                    </h1>
                    <p className="v3-body v3-sadmin-sub">{t('Manage user accounts and the role permissions assigned to them.')}</p>
                </div>
                <div className="v3-sadmin-hero-actions">
                    <span className="v3-sadmin-kpi-icon accent" aria-hidden><UsersIcon size={18} /></span>
                </div>
            </div>

            {tabs.length > 1 && (
                <div className="v3-card" style={{ padding: '4px 8px' }}>
                    <div className="v3-settings-tabs" role="tablist">
                        {tabs.map((tb) => (
                            <button
                                key={tb.id}
                                type="button"
                                role="tab"
                                aria-selected={tb.id === tab}
                                className={`v3-settings-tab ${tb.id === tab ? 'active' : ''}`}
                                onClick={() => setTab(tb.id)}
                            >
                                {t(tb.label)}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Only the active tab is mounted → remount on switch keeps data fresh
                (e.g. a new role shows up in the user form's profile picker). */}
            {tab === 'users' && <ManageUsers embedded />}
            {tab === 'roles' && <ManageProfile embedded />}
        </div>
    );
};

export default UserManagement;
